import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CircleMarker, MapContainer, Popup, TileLayer, useMap } from 'react-leaflet';
import { LocateFixed, MapPin, Navigation } from 'lucide-react';
import {
  BRUSSELS_CENTER,
  BRUSSELS_PILOT_PLACES,
  MeetingPlace,
} from '../data/brusselsPlaces';
import { formatKm, haversineKm } from '../utils/geo';
import 'leaflet/dist/leaflet.css';

type Origin = { lat: number; lng: number; source: 'geo' | 'brussels' };

function FitBounds({ origin, places }: { origin: Origin; places: MeetingPlace[] }) {
  const map = useMap();
  useEffect(() => {
    map.fitBounds(
      [[origin.lat, origin.lng], ...places.map((p) => [p.lat, p.lng] as [number, number])],
      { padding: [36, 36], maxZoom: 13 }
    );
  }, [map, origin.lat, origin.lng, places]);
  return null;
}

function FocusPlace({ place }: { place: MeetingPlace }) {
  const map = useMap();
  useEffect(() => {
    map.flyTo([place.lat, place.lng], 15, { duration: 0.55 });
  }, [map, place.lat, place.lng]);
  return null;
}

const PartnerLibrariesMap: React.FC = () => {
  const [origin, setOrigin] = useState<Origin>({ ...BRUSSELS_CENTER, source: 'brussels' });
  const [geoError, setGeoError] = useState<string | null>(null);
  const [locating, setLocating] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);

  const ranked = useMemo(
    () =>
      [...BRUSSELS_PILOT_PLACES]
        .map((place) => ({
          ...place,
          km: haversineKm(origin.lat, origin.lng, place.lat, place.lng),
        }))
        .sort((a, b) => a.km - b.km),
    [origin]
  );

  const selectedPlace = ranked.find((p) => p.name === selected) ?? null;

  const locate = useCallback(() => {
    if (!navigator.geolocation) {
      setGeoError('La géolocalisation n’est pas disponible sur cet appareil.');
      return;
    }
    setLocating(true);
    setGeoError(null);
    setSelected(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setOrigin({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          source: 'geo',
        });
        setLocating(false);
      },
      () => {
        setGeoError('Position refusée. Nous affichons le centre de Bruxelles.');
        setOrigin({ ...BRUSSELS_CENTER, source: 'brussels' });
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60_000 }
    );
  }, []);

  useEffect(() => {
    locate();
  }, [locate]);

  return (
    <div className="grid lg:grid-cols-12 gap-6 items-start">
      <div className="lg:col-span-7 card overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200">
          <p className="text-sm text-ink-muted">
            {origin.source === 'geo'
              ? 'Triées depuis votre position'
              : 'Triées depuis le centre de Bruxelles'}
          </p>
          <button
            type="button"
            onClick={locate}
            disabled={locating}
            className="btn btn-outline text-xs py-1.5 px-3"
          >
            <LocateFixed className="h-3.5 w-3.5 mr-1.5" />
            {locating ? 'Localisation…' : 'Me localiser'}
          </button>
        </div>
        {geoError && (
          <p className="px-4 py-2 text-xs text-ink-muted bg-paper-dark border-b border-gray-200">
            {geoError}
          </p>
        )}
        <div className="h-[380px] sm:h-[440px] relative z-0">
          <MapContainer
            center={[origin.lat, origin.lng]}
            zoom={12}
            scrollWheelZoom
            className="h-full w-full bs-map"
          >
            <TileLayer
              attribution='&copy; OpenStreetMap &copy; CARTO'
              url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
            />
            {selectedPlace ? (
              <FocusPlace place={selectedPlace} />
            ) : (
              <FitBounds origin={origin} places={BRUSSELS_PILOT_PLACES} />
            )}
            <CircleMarker
              center={[origin.lat, origin.lng]}
              radius={8}
              pathOptions={{ color: '#ffffff', weight: 2, fillColor: '#1E3A5F', fillOpacity: 1 }}
            >
              <Popup>{origin.source === 'geo' ? 'Vous êtes ici' : 'Centre de Bruxelles'}</Popup>
            </CircleMarker>
            {ranked.map((place) => {
              const active = place.name === selectedPlace?.name;
              return (
                <CircleMarker
                  key={place.name}
                  center={[place.lat, place.lng]}
                  radius={active ? 11 : 7}
                  pathOptions={{
                    color: '#ffffff',
                    weight: 2,
                    fillColor: active ? '#1C2F28' : '#2C4A3E',
                    fillOpacity: 1,
                  }}
                  eventHandlers={{ click: () => setSelected(place.name) }}
                >
                  <Popup>
                    <strong className="block text-sm">{place.name}</strong>
                    <span className="text-xs">{place.address}</span>
                    <br />
                    <span className="text-xs">{formatKm(place.km)}</span>
                  </Popup>
                </CircleMarker>
              );
            })}
          </MapContainer>
        </div>
      </div>

      <div className="lg:col-span-5">
        <p className="text-xs font-semibold tracking-widest uppercase text-forest-600 mb-3">
          Plus proches
        </p>
        <ul className="space-y-2">
          {ranked.map((place, index) => {
            const active = place.name === selectedPlace?.name;
            return (
              <li key={place.name}>
                <button
                  type="button"
                  onClick={() => setSelected(place.name)}
                  className={`w-full text-left card p-4 transition-colors ${
                    active ? 'border-forest-600 bg-forest-50' : 'hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-forest-100 text-xs font-medium text-forest-800">
                        {index + 1}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-ink leading-snug">{place.name}</p>
                        <p className="text-xs text-ink-muted mt-0.5">{place.commune}</p>
                        <p className="text-xs text-ink-faint mt-1 leading-relaxed">{place.address}</p>
                      </div>
                    </div>
                    <span className="shrink-0 text-xs font-medium text-forest-700">
                      {formatKm(place.km)}
                    </span>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
        {selectedPlace && (
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${selectedPlace.lat},${selectedPlace.lng}`}
            target="_blank"
            rel="noreferrer"
            className="btn btn-primary w-full mt-4"
          >
            <Navigation className="h-4 w-4 mr-2" />
            Itinéraire
          </a>
        )}
        <p className="mt-3 text-xs text-ink-faint flex items-start gap-2">
          <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          Remise dans le hall, aux heures d’ouverture. Pas de rendez-vous dans la rue.
        </p>
      </div>
    </div>
  );
};

export default PartnerLibrariesMap;
